import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { useTranslation } from '@/lib/i18n';
import {
  Badge,
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Input,
  RadixSelect,
  SectionCard,
  Skeleton,
  StatusBadge,
  Switch,
  toast,
  type SelectOption,
} from '@/components/ui';
import { nationalityOptions } from '@/lib/countries/nationalityOptions';
import { useHasRole } from '@/providers/AuthProvider';
import { NOTIFICATION_INTEGRATION_WRITE_ROLES } from '../lib/notificationActionRbac';
import { languageLabel } from '../lib/notificationLabels';
import {
  useDeleteKakaoChannelMutation,
  useDeleteSenderNumberMutation,
  useKakaoChannelsQuery,
  useKakaoDispatchConfigQuery,
  useSendRestrictionQuery,
  useSenderNumbersQuery,
  useUpdateKakaoChannelMutation,
  useUpdateSenderNumberMutation,
  useUpdateSendRestrictionMutation,
  useLocaleMappingQuery,
  useUpdateLocaleMappingMutation,
} from '../hooks/useIntegrationQuery';
import type {
  InspectionStatus,
  KakaoChannel,
  SenderNumber,
  SendRestriction,
  VerificationStatus,
  LocaleMappingEntry,
  NotificationLocale,
} from '../types/notificationIntegration';
import { RegisterSenderNumberModal } from '../components/RegisterSenderNumberModal';
import { RegisterKakaoChannelModal } from '../components/RegisterKakaoChannelModal';
import { RecipientAllowlistSection } from '../components/RecipientAllowlistSection';

const VERIFY_VARIANT: Record<VerificationStatus, 'success' | 'pending' | 'danger' | 'neutral'> = {
  VERIFIED: 'success',
  PENDING: 'pending',
  FAILED: 'danger',
  UNVERIFIED: 'neutral',
};
const INSPECTION_VARIANT: Record<InspectionStatus, 'success' | 'pending' | 'danger'> = {
  APPROVED: 'success',
  REQUESTED: 'pending',
  REJECTED: 'danger',
};

const formatDate = (iso: string): string => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('ko-KR');
  } catch {
    return '—';
  }
};

/** 알림톡 현재 발송 설정(서버 설정값) — 실제 알림톡이 나가는 채널·발신프로필. */
export const KakaoDispatchConfigCard = ({
  query,
  channels,
}: {
  query: ReturnType<typeof useKakaoDispatchConfigQuery>;
  channels: ReadonlyArray<{ kakaoChannelId: string; channelName: string }>;
}) => {
  const { t } = useTranslation('notifications');
  const box = 'mb-3 rounded-md border px-3 py-2 text-[12px]';
  const boxStyle = { borderColor: 'var(--divider-strong)', background: 'var(--surface-1)' } as const;
  if (query.isPending) return <Skeleton className="mb-3 h-[56px] w-full" />;
  if (query.isError || !query.data) {
    const notReady = (query.error as { status?: number } | null)?.status === 404;
    return (
      <p className={box} style={{ ...boxStyle, color: 'var(--content-muted)' }} data-testid="kakao-dispatch-config-unavailable">
        {notReady
          ? t('integration.kakao.current.notReady', '현재 발송 설정 조회는 서버(BE) 반영 후 표시됩니다.')
          : t('integration.kakao.current.error', '현재 발송 설정을 불러오지 못했습니다.')}
      </p>
    );
  }
  const c = query.data;
  const matched = c.matchedKakaoChannelId ? channels.find((ch) => ch.kakaoChannelId === c.matchedKakaoChannelId) : null;
  // BE 회신 — 서버 발신프로필과 같은 키로 등록된 BO 채널이 있으면 그 채널 카드로 이동한다.
  const goToMatched = () => {
    const el = document.getElementById(`kakao-channel-${c.matchedKakaoChannelId}`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el?.focus({ preventScroll: true });
  };
  const rows: Array<[string, ReactNode]> = [
    [t('integration.kakao.current.channel', '채널'), [c.channelName, c.searchId].filter(Boolean).join(' / ') || '—'],
    [t('integration.kakao.current.senderKey', '발신프로필키'), c.maskedSenderKey ?? '—'],
    [
      t('integration.kakao.current.mode', '발송 방식'),
      c.liveDispatch
        ? t('integration.kakao.current.live', '실발송')
        : t('integration.kakao.current.sandbox', '테스트(실제로 나가지 않음)'),
    ],
    [t('integration.kakao.current.callback', '대체 문자 발신번호'), c.maskedCallbackNumber ?? '—'],
    [
      t('integration.kakao.current.registered', 'BO 등록 채널'),
      matched ? (
        <button
          type="button"
          className="font-sans underline underline-offset-2"
          style={{ color: 'var(--accent-default)' }}
          onClick={goToMatched}
          data-testid="kakao-dispatch-config-matched"
        >
          {matched.channelName}
        </button>
      ) : (
        t('integration.kakao.current.notRegistered', '같은 발신프로필로 등록된 채널 없음')
      ),
    ],
  ];
  return (
    <div className={box} style={boxStyle} data-testid="kakao-dispatch-config">
      <p className="mb-1.5 font-semibold" style={{ color: 'var(--content-strong)' }}>
        {t('integration.kakao.current.title', '현재 발송 설정 (서버)')}
        {c.provider ? <span className="ml-1 font-normal" style={{ color: 'var(--content-muted)' }}>· {c.provider}</span> : null}
      </p>
      <dl className="grid grid-cols-[120px_1fr] gap-x-3 gap-y-1">
        {rows.map(([k, v]) => (
          <div key={k} className="contents">
            <dt style={{ color: 'var(--content-muted)' }}>{k}</dt>
            <dd className="font-mono" style={{ color: 'var(--content-strong)' }}>{v}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
};

/** 알림톡·문자 연동 탭 — 카카오 채널 / 발신번호 / 발송 제한 (디자인 스펙 §7). */
export const IntegrationTab = () => {
  const { t } = useTranslation('notifications');
  const canWrite = useHasRole(NOTIFICATION_INTEGRATION_WRITE_ROLES);

  const [senderOpen, setSenderOpen] = useState(false);
  const [kakaoOpen, setKakaoOpen] = useState(false);
  const [senderDelete, setSenderDelete] = useState<SenderNumber | null>(null);
  const [kakaoDelete, setKakaoDelete] = useState<KakaoChannel | null>(null);

  const senders = useSenderNumbersQuery();
  const kakao = useKakaoChannelsQuery();
  const dispatchConfig = useKakaoDispatchConfigQuery();
  const restriction = useSendRestrictionQuery();

  const updateSender = useUpdateSenderNumberMutation();
  const removeSender = useDeleteSenderNumberMutation();
  const updateKakao = useUpdateKakaoChannelMutation();
  const removeKakao = useDeleteKakaoChannelMutation();

  // 기본 설정 = isDefault:true PUT 편집(전용 엔드포인트 없음).
  const setDefaultSender = (s: SenderNumber) =>
    updateSender.mutate(
      { senderNumberId: s.senderNumberId, body: { isDefault: true } },
      {
        onSuccess: () => toast.success(t('integration.sender.toast.defaultSuccess', '기본 발신번호가 변경되었습니다.')),
        onError: (e) => toast.error(e.message ?? t('integration.sender.toast.defaultFail', '기본 발신번호 변경에 실패했습니다.')),
      },
    );
  const setDefaultKakao = (c: KakaoChannel) =>
    updateKakao.mutate(
      { kakaoChannelId: c.kakaoChannelId, body: { isDefault: true } },
      {
        onSuccess: () => toast.success(t('integration.kakao.toast.defaultSuccess', '기본 카카오 채널이 변경되었습니다.')),
        onError: (e) => toast.error(e.message ?? t('integration.kakao.toast.defaultFail', '기본 카카오 채널 변경에 실패했습니다.')),
      },
    );

  return (
    <div className="flex flex-col gap-4" data-testid="integration-tab">
      <p
        className="rounded-md p-3 text-[13px]"
        style={{ background: 'var(--status-info-bg)', color: 'var(--status-info-fg)' }}
        role="note"
      >
        {t('integration.subtitle', '발신번호·카카오 채널을 등록하고 발송 제한을 설정합니다.')}
      </p>

      {/* 카카오 채널 */}
      <SectionCard
        title={t('integration.kakao.title', '카카오 채널')}
        description={t('integration.kakao.description', '알림톡 발송에 사용할 카카오 채널(발신프로필)을 등록합니다.')}
        aside={
          canWrite ? (
            <Button size="sm" onClick={() => setKakaoOpen(true)}>
              <Plus size={14} aria-hidden="true" />
              {t('integration.kakao.add', '카카오 채널 등록')}
            </Button>
          ) : undefined
        }
      >
        {/* notification #28 회신 — 알림톡 발신프로필키는 BE 서버 설정값이 기준. 이 화면은 채널 정보 관리용(발송에 쓰이지 않음). */}
        <p
          className="mb-3 rounded-md px-3 py-2 text-[12px]"
          style={{ background: 'var(--status-info-bg, var(--surface-1))', color: 'var(--content-default)' }}
          data-testid="kakao-channel-notice"
        >
          {t('integration.kakao.notice', '이 화면은 카카오 채널 정보 관리용입니다. 실제 알림톡 발송은 서버에 설정된 발신프로필로 이루어집니다.')}
        </p>
        <KakaoDispatchConfigCard query={dispatchConfig} channels={kakao.data ?? []} />
        {kakao.isPending ? (
          <Skeleton className="h-[64px] w-full" />
        ) : kakao.isError ? (
          <EmptyState size="inline" title={t('integration.kakao.error', '카카오 채널 목록을 불러오지 못했습니다.')} />
        ) : kakao.data && kakao.data.length > 0 ? (
          <div className="flex flex-col gap-2">
            {kakao.data.map((ch) => (
              <div
                key={ch.kakaoChannelId}
                id={`kakao-channel-${ch.kakaoChannelId}`}
                tabIndex={-1}
                className="flex flex-col gap-1 rounded-md p-3 outline-none focus-visible:[box-shadow:var(--focus-ring-brand)]"
                style={{ border: '1px solid var(--divider-strong)' }}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="text-[14px] font-semibold" style={{ color: 'var(--content-strong)' }}>{ch.channelName}</span>
                    {ch.isDefault ? <Badge variant="neutral" size="sm">{t('integration.kakao.default', '기본 채널')}</Badge> : null}
                    <StatusBadge
                      variant={INSPECTION_VARIANT[ch.inspectionStatus]}
                      label={ch.inspectionStatusLabel ?? t(`integration.kakao.status.${ch.inspectionStatus === 'APPROVED' ? 'APPROVED' : ch.inspectionStatus === 'REJECTED' ? 'REJECTED' : 'PENDING'}`)}
                    />
                  </div>
                  {canWrite ? (
                    <div className="flex items-center gap-1.5">
                      {!ch.isDefault ? (
                        <Button variant="outline" size="sm" disabled={updateKakao.isPending} onClick={() => setDefaultKakao(ch)}>
                          {t('integration.kakao.setDefault', '기본으로 설정')}
                        </Button>
                      ) : null}
                      <Button variant="danger" size="sm" onClick={() => setKakaoDelete(ch)}>{t('integration.kakao.delete', '삭제')}</Button>
                    </div>
                  ) : null}
                </div>
                <div className="flex flex-wrap gap-x-4 text-[12px]" style={{ color: 'var(--content-muted)' }}>
                  {ch.searchId ? <span>{ch.searchId}</span> : null}
                  <span>{formatDate(ch.createdAt)}</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState size="inline" title={t('integration.kakao.empty', '등록된 카카오 채널이 없습니다.')} />
        )}
      </SectionCard>

      {/* 발신번호 */}
      <SectionCard
        title={t('integration.sender.title', '발신번호')}
        description={t('integration.sender.description', '문자(SMS/LMS/MMS) 발송에 사용할 발신번호를 등록·인증합니다.')}
        aside={
          canWrite ? (
            <Button size="sm" onClick={() => setSenderOpen(true)}>
              <Plus size={14} aria-hidden="true" />
              {t('integration.sender.add', '발신번호 등록')}
            </Button>
          ) : undefined
        }
      >
        {senders.isPending ? (
          <Skeleton className="h-[64px] w-full" />
        ) : senders.isError ? (
          <EmptyState size="inline" title={t('integration.sender.error', '발신번호 목록을 불러오지 못했습니다.')} />
        ) : senders.data && senders.data.length > 0 ? (
          <div className="flex flex-col gap-2">
            {senders.data.map((s) => (
              <div key={s.senderNumberId} className="flex flex-col gap-1 rounded-md p-3" style={{ border: '1px solid var(--divider-strong)' }}>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="text-[14px] font-semibold tabular-nums" style={{ color: 'var(--content-strong)' }}>{s.maskedPhoneNumber}</span>
                    {s.isDefault ? <Badge variant="neutral" size="sm">{t('integration.sender.default', '기본 번호')}</Badge> : null}
                    <StatusBadge
                      variant={VERIFY_VARIANT[s.verificationStatus]}
                      label={s.verificationStatusLabel ?? t(`integration.sender.status.${s.verificationStatus}`)}
                    />
                  </div>
                  {canWrite ? (
                    <div className="flex items-center gap-1.5">
                      {!s.isDefault && s.verificationStatus === 'VERIFIED' ? (
                        <Button variant="outline" size="sm" disabled={updateSender.isPending} onClick={() => setDefaultSender(s)}>
                          {t('integration.sender.setDefault', '기본으로 설정')}
                        </Button>
                      ) : null}
                      <Button variant="danger" size="sm" onClick={() => setSenderDelete(s)}>{t('integration.sender.delete', '삭제')}</Button>
                    </div>
                  ) : null}
                </div>
                <div className="flex flex-wrap gap-x-4 text-[12px]" style={{ color: 'var(--content-muted)' }}>
                  <span>{s.description ?? s.purposeLabel ?? '—'}</span>
                  <span>{formatDate(s.createdAt)}</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState size="inline" title={t('integration.sender.empty', '등록된 발신번호가 없습니다.')} />
        )}
      </SectionCard>

      <SendRestrictionSection canWrite={canWrite} isPending={restriction.isPending} isError={restriction.isError} data={restriction.data} />

      {/* 수신자 허용 목록(테스트 모드) — 발송 제한과 별개 API. 테스트 중 실제 고객에게 알림이 나가지 않게. */}
      <RecipientAllowlistSection canWrite={canWrite} />

      <LocaleMappingSection canWrite={canWrite} />

      <RegisterSenderNumberModal open={senderOpen} onOpenChange={setSenderOpen} />
      <RegisterKakaoChannelModal open={kakaoOpen} onOpenChange={setKakaoOpen} />

      {/* 발신번호 삭제 확인 */}
      <Dialog open={senderDelete !== null} onOpenChange={(o) => !o && setSenderDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('integration.sender.deleteDialog.title', '발신번호 삭제')}</DialogTitle>
            <DialogDescription>
              {t('integration.sender.deleteDialog.description', '이 발신번호를 삭제하시겠습니까? 이 번호를 사용하는 발송 설정은 발신번호를 다시 지정해야 합니다.')}
            </DialogDescription>
          </DialogHeader>
          {senderDelete ? <p className="tabular-nums text-[13px]" style={{ color: 'var(--content-muted)' }}>{senderDelete.maskedPhoneNumber}</p> : null}
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" size="sm">{t('common.cancel', '취소')}</Button>
            </DialogClose>
            <Button
              variant="danger"
              size="sm"
              disabled={removeSender.isPending}
              onClick={() => {
                if (!senderDelete) return;
                removeSender.mutate(senderDelete.senderNumberId, {
                  onSuccess: () => {
                    toast.success(t('integration.sender.toast.deleteSuccess', '발신번호가 삭제되었습니다.'));
                    setSenderDelete(null);
                  },
                  onError: (e) => toast.error(e.message ?? t('integration.sender.toast.deleteFail', '발신번호 삭제에 실패했습니다.')),
                });
              }}
            >
              {t('common.delete', '삭제')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 카카오 채널 삭제 확인 */}
      <Dialog open={kakaoDelete !== null} onOpenChange={(o) => !o && setKakaoDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('integration.kakao.deleteDialog.title', '카카오 채널 삭제')}</DialogTitle>
            <DialogDescription>
              {t('integration.kakao.deleteDialog.description', '이 카카오 채널을 삭제하시겠습니까? 이 채널을 사용하는 발송 설정은 채널을 다시 지정해야 합니다.')}
            </DialogDescription>
          </DialogHeader>
          {kakaoDelete ? <p className="text-[13px]" style={{ color: 'var(--content-muted)' }}>{kakaoDelete.channelName}</p> : null}
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" size="sm">{t('common.cancel', '취소')}</Button>
            </DialogClose>
            <Button
              variant="danger"
              size="sm"
              disabled={removeKakao.isPending}
              onClick={() => {
                if (!kakaoDelete) return;
                removeKakao.mutate(kakaoDelete.kakaoChannelId, {
                  onSuccess: () => {
                    toast.success(t('integration.kakao.toast.deleteSuccess', '카카오 채널이 삭제되었습니다.'));
                    setKakaoDelete(null);
                  },
                  onError: (e) => toast.error(e.message ?? t('integration.kakao.toast.deleteFail', '카카오 채널 삭제에 실패했습니다.')),
                });
              }}
            >
              {t('common.delete', '삭제')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

interface SendRestrictionSectionProps {
  canWrite: boolean;
  isPending: boolean;
  isError: boolean;
  data: SendRestriction | undefined;
}

const SendRestrictionSection = ({ canWrite, isPending, isError, data }: SendRestrictionSectionProps) => {
  const { t } = useTranslation('notifications');
  const update = useUpdateSendRestrictionMutation();
  const [form, setForm] = useState<SendRestriction>({
    nightBlockEnabled: false,
    nightStartTime: '21:00',
    nightEndTime: '08:00',
    duplicateBlockEnabled: false,
    duplicateWindowMinutes: 60,
  });

  useEffect(() => {
    if (data) setForm(data);
  }, [data]);

  const onSave = () => {
    update.mutate(form, {
      onSuccess: () => toast.success(t('integration.restriction.toast.saveSuccess', '발송 제한 설정이 저장되었습니다.')),
      onError: (e) => toast.error(e.message ?? t('integration.restriction.toast.saveFail', '발송 제한 설정에 실패했습니다.')),
    });
  };

  return (
    <SectionCard
      title={t('integration.restriction.title', '발송 제한')}
      description={t('integration.restriction.description', '광고성·자동 발송에 적용되는 야간 발송 차단·중복 발송 차단을 설정합니다.')}
    >
      {isPending ? (
        <Skeleton className="h-[120px] w-full" />
      ) : isError ? (
        <EmptyState size="inline" title={t('integration.restriction.error', '발송 제한 설정을 불러오지 못했습니다.')} />
      ) : (
        <div className="flex flex-col gap-5 rounded-md p-4" style={{ background: 'var(--surface-1)' }}>
          {/* 야간 차단 */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <div className="flex flex-col">
                <span className="text-[13px] font-medium" style={{ color: 'var(--content-strong)' }}>{t('integration.restriction.night.enabled', '야간 발송 차단')}</span>
                <span className="text-[12px]" style={{ color: 'var(--content-muted)' }}>{t('integration.restriction.night.hint', '광고성 메시지를 지정한 시간대에 발송하지 않습니다 (정보통신망법 권고 21시~익일 08시).')}</span>
              </div>
              <Switch checked={form.nightBlockEnabled} disabled={!canWrite} onCheckedChange={(v) => setForm((f) => ({ ...f, nightBlockEnabled: v }))} aria-label={t('integration.restriction.night.enabled', '야간 발송 차단')} />
            </div>
            {form.nightBlockEnabled ? (
              <div className="flex items-center gap-2">
                <Input type="time" size="sm" className="w-28" value={form.nightStartTime} disabled={!canWrite} onChange={(e) => setForm((f) => ({ ...f, nightStartTime: e.target.value }))} aria-label={t('integration.restriction.night.start', '차단 시작')} />
                <span style={{ color: 'var(--content-muted)' }}>~</span>
                <Input type="time" size="sm" className="w-28" value={form.nightEndTime} disabled={!canWrite} onChange={(e) => setForm((f) => ({ ...f, nightEndTime: e.target.value }))} aria-label={t('integration.restriction.night.end', '차단 종료')} />
              </div>
            ) : null}
          </div>

          {/* 중복 차단 */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <div className="flex flex-col">
                <span className="text-[13px] font-medium" style={{ color: 'var(--content-strong)' }}>{t('integration.restriction.dedupe.enabled', '중복 발송 차단')}</span>
                <span className="text-[12px]" style={{ color: 'var(--content-muted)' }}>{t('integration.restriction.dedupe.hint', '동일 수신자에게 지정한 시간 내 같은 메시지를 중복 발송하지 않습니다.')}</span>
              </div>
              <Switch checked={form.duplicateBlockEnabled} disabled={!canWrite} onCheckedChange={(v) => setForm((f) => ({ ...f, duplicateBlockEnabled: v }))} aria-label={t('integration.restriction.dedupe.enabled', '중복 발송 차단')} />
            </div>
            {form.duplicateBlockEnabled ? (
              <div className="flex items-center gap-2">
                <Input type="number" size="sm" className="w-24" min={0} max={1440} value={String(form.duplicateWindowMinutes)} disabled={!canWrite} onChange={(e) => setForm((f) => ({ ...f, duplicateWindowMinutes: Math.max(0, Math.min(1440, Number(e.target.value) || 0)) }))} aria-label={t('integration.restriction.dedupe.window', '중복 차단 시간(분)')} />
                <span className="text-[12px]" style={{ color: 'var(--content-muted)' }}>{t('integration.restriction.dedupe.window', '중복 차단 시간(분)')}</span>
              </div>
            ) : null}
          </div>

          {canWrite ? (
            <div className="flex justify-end">
              <Button size="sm" disabled={update.isPending} onClick={onSave}>
                {update.isPending ? t('integration.restriction.saving', '저장 중…') : t('integration.restriction.save', '저장')}
              </Button>
            </div>
          ) : null}
        </div>
      )}
    </SectionCard>
  );
};

const LOCALE_VALUES: ReadonlyArray<NotificationLocale> = ['KO_KR', 'EN_US', 'JA_JP', 'ZH_CN'];

/** 국적별 발송 언어(로케일) 매핑 — 수신자 국적 → 발송 템플릿 언어. 그 외 국가는 기본 언어. (V24) */
const LocaleMappingSection = ({ canWrite }: { canWrite: boolean }) => {
  const { t } = useTranslation('notifications');
  const query = useLocaleMappingQuery();
  const update = useUpdateLocaleMappingMutation();

  const [defaultLocale, setDefaultLocale] = useState<NotificationLocale>('EN_US');
  const [rows, setRows] = useState<LocaleMappingEntry[]>([]);
  const [addCode, setAddCode] = useState('');

  useEffect(() => {
    if (query.data) {
      setDefaultLocale(query.data.defaultLocale);
      setRows(query.data.mappings);
    }
  }, [query.data]);

  const countryOptions = useMemo(() => nationalityOptions('ko-KR'), []);
  const countryLabelOf = useMemo(() => {
    const map = new Map(countryOptions.map((o) => [o.value, o.label]));
    return (code: string): string => map.get(code) ?? code;
  }, [countryOptions]);
  const localeOptions = useMemo<SelectOption[]>(
    () => LOCALE_VALUES.map((l) => ({ value: l, label: languageLabel(t, l) })),
    [t],
  );
  const assigned = useMemo(() => new Set(rows.map((r) => r.countryCode)), [rows]);
  const addOptions = useMemo<SelectOption[]>(
    () => [
      { value: '', label: t('localeMapping.addPlaceholder', '국가 선택') },
      ...countryOptions.filter((o) => o.value !== '' && !assigned.has(o.value)),
    ],
    [countryOptions, assigned, t],
  );

  const pickCountry = (code: string) => {
    if (code && !assigned.has(code)) {
      setRows((prev) => [...prev, { countryCode: code, locale: 'KO_KR' }]);
    }
    setAddCode('');
  };

  const onSave = () => {
    update.mutate(
      { defaultLocale, mappings: rows },
      {
        onSuccess: () => toast.success(t('localeMapping.toast.saveSuccess', '발송 언어 매핑이 저장되었습니다.')),
        onError: (e) => toast.error(e.message ?? t('localeMapping.toast.saveFail', '발송 언어 매핑 저장에 실패했습니다.')),
      },
    );
  };

  return (
    <SectionCard
      title={t('localeMapping.title', '국적별 발송 언어')}
      description={t('localeMapping.description', '수신자 국적에 따라 해당 언어의 템플릿으로 발송합니다. 배정되지 않은 국가는 기본 언어로 발송됩니다.')}
    >
      {query.isPending ? (
        <Skeleton className="h-[160px] w-full" />
      ) : query.isError ? (
        <EmptyState size="inline" title={t('localeMapping.error', '발송 언어 매핑을 불러오지 못했습니다.')} />
      ) : (
        <div className="flex flex-col gap-5 rounded-md p-4" style={{ background: 'var(--surface-1)' }}>
          {/* 그 외 모든 국가 → 기본 언어 */}
          <div className="flex items-center justify-between gap-3">
            <div className="flex flex-col">
              <span className="text-[13px] font-medium" style={{ color: 'var(--content-strong)' }}>
                {t('localeMapping.default.label', '그 외 모든 국가')}
              </span>
              <span className="text-[12px]" style={{ color: 'var(--content-muted)' }}>
                {t('localeMapping.default.hint', '아래에 배정되지 않은 모든 국가의 발송 언어입니다.')}
              </span>
            </div>
            <div className="w-40 shrink-0">
              <RadixSelect
                value={defaultLocale}
                onChange={(v) => setDefaultLocale(v as NotificationLocale)}
                options={localeOptions}
                disabled={!canWrite}
                aria-label={t('localeMapping.default.label', '그 외 모든 국가')}
              />
            </div>
          </div>

          {/* 국가별 배정 */}
          <div className="flex flex-col gap-2">
            <span className="text-[12px] font-medium" style={{ color: 'var(--content-muted)' }}>
              {t('localeMapping.assignedTitle', '국가별 배정')}
            </span>
            {rows.length === 0 ? (
              <EmptyState size="inline" title={t('localeMapping.empty', '배정된 국가가 없습니다. 모든 국가가 기본 언어로 발송됩니다.')} />
            ) : (
              <div className="flex flex-col gap-1.5">
                {rows.map((r) => (
                  <div key={r.countryCode} className="flex items-center gap-2">
                    <span
                      className="inline-flex h-8 min-w-[160px] flex-1 items-center rounded-md px-2.5 text-[13px]"
                      style={{ background: 'var(--surface-0)', border: '1px solid var(--divider-strong)', color: 'var(--content-default)' }}
                    >
                      {countryLabelOf(r.countryCode)}
                    </span>
                    <div className="w-40 shrink-0">
                      <RadixSelect
                        value={r.locale}
                        onChange={(v) =>
                          setRows((prev) =>
                            prev.map((x) => (x.countryCode === r.countryCode ? { ...x, locale: v as NotificationLocale } : x)),
                          )
                        }
                        options={localeOptions}
                        disabled={!canWrite}
                        aria-label={countryLabelOf(r.countryCode)}
                      />
                    </div>
                    {canWrite ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setRows((prev) => prev.filter((x) => x.countryCode !== r.countryCode))}
                        aria-label={t('common.delete', '삭제')}
                      >
                        <Trash2 size={13} aria-hidden="true" style={{ color: 'var(--signal-danger)' }} />
                      </Button>
                    ) : null}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* 국가 추가 */}
          {canWrite ? (
            <div className="flex items-center gap-2">
              <div className="w-56">
                <RadixSelect
                  value={addCode}
                  onChange={pickCountry}
                  options={addOptions}
                  aria-label={t('localeMapping.add', '국가 추가')}
                />
              </div>
              <span className="text-[12px]" style={{ color: 'var(--content-muted)' }}>
                {t('localeMapping.addHint', '국가를 선택하면 목록에 추가됩니다. 추가 후 발송 언어를 지정하세요.')}
              </span>
            </div>
          ) : null}

          {canWrite ? (
            <div className="flex justify-end">
              <Button size="sm" disabled={update.isPending} onClick={onSave}>
                {update.isPending ? t('localeMapping.saving', '저장 중…') : t('localeMapping.save', '저장')}
              </Button>
            </div>
          ) : null}
        </div>
      )}
    </SectionCard>
  );
};
